import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { HoruzProtocolChulakCloseCode } from '@ver.id/libs.internal.horuz';
import {
  ChulakState,
  ChulakContext,
  ChulakActions,
  connectChulak,
  isTransientDisconnect,
} from '../chulak/protocol';
import { JourneyContext } from './useJourney';
import { useCancelConfirm } from './useCancelConfirm';
import { resolveAppNameWithArticle } from '../data/handlerApps';
import { useTranslation } from '../i18n/i18n';
import { type SessionData, type RequestToken, type AppInfo } from '../types';

type UseChulakSessionResult = {
  ctx: ChulakContext;
  actions: ChulakActions | null;
  app: AppInfo | undefined;
  handleCancel: () => Promise<void>;
  handleComplete: () => Promise<void>;
  handleRestart: () => void;
  interactionConfig: SessionData['interactionConfig'];
};

/**
 * useChulakSession — Shared hook encapsulating the Chulak WebSocket connection,
 * state machine, retry logic, and cancel/complete/restart handlers.
 *
 * Used by both MobileChulakSession and DesktopChulakSession.
 */
export function useChulakSession(
  session: SessionData,
  selectedToken: RequestToken,
): UseChulakSessionResult {
  const journey = useContext(JourneyContext)!;
  const handleCancel = useCancelConfirm();
  const { locale } = useTranslation();
  const [chulakCtx, setChulakCtx] = useState<ChulakContext>({
    state: ChulakState.Init,
  });
  const actionsRef = useRef<ChulakActions | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [restartCount, setRestartCount] = useState(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const MAX_RETRIES = 3;

  const request = session.context.request;
  const isIssuance = request.type === 'issuance';
  const interactionConfig = useMemo(
    () =>
      isIssuance
        ? { ...session.interactionConfig, requireExplicitConsent: false }
        : session.interactionConfig,
    [isIssuance, session.interactionConfig],
  );

  const appUuid = selectedToken.payload.body.handlerAppUuid;
  const appInfo = appUuid ? session.handlerApps[appUuid] : undefined;
  const app: AppInfo | undefined = appInfo
    ? {
        name: appInfo.name,
        nameWithArticle: resolveAppNameWithArticle(appInfo, locale),
        logo: appInfo.base64Logo,
      }
    : undefined;

  useEffect(() => {
    const actions = connectChulak(
      selectedToken.payload.body.handler,
      selectedToken,
      {
        fresh: retryCount === 0,
        onStateChange: (ctx) => {
          if (journey.isRedirecting()) return;

          if (isTransientDisconnect(ctx) && retryCount < MAX_RETRIES) {
            const delay = Math.min(1000 * Math.pow(2, retryCount), 8000);
            retryTimerRef.current = setTimeout(
              () => setRetryCount((c) => c + 1),
              delay,
            );
            return;
          }

          if (isTransientDisconnect(ctx) && retryCount >= MAX_RETRIES) {
            setChulakCtx({
              state: ChulakState.Abort,
              errorCode: HoruzProtocolChulakCloseCode.AGENT_DISCONNECT,
            });
            return;
          }

          setChulakCtx(ctx);
        },
        interactionConfig,
      },
    );
    actionsRef.current = actions;

    journey.registerChulak({ abort: actions.abort, cleanup: actions.cleanup });

    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (!journey.isRedirecting()) {
        actions.cleanup();
      }
      journey.unregisterChulak();
      actionsRef.current = null;
    };
  }, [selectedToken, interactionConfig, retryCount, restartCount]);

  // When the wallet app cancels, skip abort screen and redirect immediately
  useEffect(() => {
    if (chulakCtx.state !== ChulakState.Abort) return;
    const code = chulakCtx.errorCode;
    if (
      code === HoruzProtocolChulakCloseCode.AGENT_CANCEL ||
      code === HoruzProtocolChulakCloseCode.APP_CANCEL
    ) {
      journey.reject();
    }
  }, [chulakCtx, journey]);

  const handleComplete = useCallback(async () => {
    await journey.complete();
  }, [journey]);

  const handleRestart = useCallback(() => {
    setRetryCount(0);
    setRestartCount((c) => c + 1);
    setChulakCtx({ state: ChulakState.Init });
  }, []);

  return {
    ctx: chulakCtx,
    actions: actionsRef.current,
    app,
    handleCancel,
    handleComplete,
    handleRestart,
    interactionConfig,
  };
}
